// What the Stats window holds (CLAUDE.md: The Stats modal).
//
// A tab per game, the daily's first. Played, Won and Win rate — and Streak and
// Max streak on the daily, because a streak belongs to a daily game and Call
// Sheet is a pool. Then the site's one guess-distribution chart, then the note
// about where these numbers live.
//
// **The figures are this device's own**, worked out from its record of finished
// games (lib/history.ts): only games that count — a daily played on its day, a
// Call Sheet's first completion. A new device sees "Nothing recorded yet."
// Preview mode fills them with invented figures (src/dev/preview.ts).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ModalTabs } from './Modal.tsx';
import { Distribution, MISSED, guessLabel, type DistRow } from './Distribution.tsx';
import { PREVIEW, DEVICE_STATS } from '../dev/preview.ts';
import { sharingResults, statsFor } from '../lib/history.ts';
import { useTodaysRunningOrder } from '../games/running-order/today.ts';
import './StatsPanel.css';

type GameId = 'running-order' | 'call-sheet';

const GAMES: { id: GameId; name: string; daily: boolean }[] = [
  { id: 'running-order', name: 'Running Order', daily: true },
  { id: 'call-sheet', name: 'Call Sheet', daily: false }
];

interface Figure { label: string; value: string }

// **It opens on the game you are on**, and on Running Order anywhere else — the
// same rule as the Archive and How to play windows (`initial`, from App.tsx).
export function StatsPanel({ onClose, initial = 'running-order' }: { onClose: () => void; initial?: GameId }) {
  const [tab, setTab] = useState<GameId>(initial);
  const game = GAMES.find((g) => g.id === tab)!;
  const today = useTodaysRunningOrder();
  const stats = PREVIEW ? DEVICE_STATS[tab] : statsFor(tab, tab === 'running-order' ? today : null);

  const figures: Figure[] = stats
    ? [
      { label: 'Played', value: String(stats.played) },
      { label: 'Won', value: String(stats.won) },
      { label: 'Win rate', value: `${Math.round((stats.won / stats.played) * 100)}%` },
      // The streak belongs to the daily game; a pool has none.
      ...(game.daily
        ? [{ label: 'Streak', value: String(stats.streak ?? 0) },
           { label: 'Max streak', value: String(stats.max ?? 0) }]
        : [])
    ]
    : [];

  // Counts here, not percentages — but the bars are still a share of every
  // result, never of the tallest bar.
  const rows: DistRow[] = stats
    ? (() => {
      const counts = [...stats.dist, stats.missed];
      const total = Math.max(1, counts.reduce((a, b) => a + b, 0));
      return counts.map((count, i) => ({
        label: i < stats.dist.length ? guessLabel(i + 1) : MISSED,
        value: String(count),
        pct: Math.round((count / total) * 100)
      }));
    })()
    : [];

  return (
    <>
      <ModalTabs tabs={GAMES} selected={tab} onSelect={setTab} />

      {stats ? (
        <>
          <div className={`stat-figures${figures.length === 5 ? ' is-five' : ''}`}>
            {figures.map((f) => (
              <span className="stat-figure" key={f.label}>
                <span className="stat-value">{f.value}</span>
                <span className="stat-label">{f.label}</span>
              </span>
            ))}
          </div>
          <h3 className="heading stat-heading">Guess distribution</h3>
          <Distribution rows={rows} />
        </>
      ) : (
        // A new player, and everybody until results are recorded.
        <div className="stat-empty">
          <p className="stat-empty-line">Nothing recorded yet.</p>
          <p className="stat-empty-note">
            Finish a round of {game.name} and your played, won and win rate appear here.
          </p>
          <Link className="btn btn-primary btn-block stat-play" to={`/${game.id}`} onClick={onClose}>
            Play {game.name}
          </Link>
        </div>
      )}

      {/* Never "nothing is sent anywhere": results are posted for the aggregate
          panels (CLAUDE.md: The Stats modal footer). */}
      <p className="modal-foot stat-foot">
        {/* With "Include my results in site statistics" off (the Privacy page),
            the second sentence would not be true, so it says what is. */}
        {sharingResults()
          ? "Your stats are stored on this device. Anonymous results count toward each puzzle's statistics."
          : 'Your stats are stored on this device. Your results are not recorded on our server.'}
      </p>
    </>
  );
}
